import { useEffect } from 'react';
import { Box, Container, Typography, Button, Stack, Card, CardContent, Grid, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { HeroSection } from '../../components/marketing/HeroSection';
import { TestimonialCarousel } from '../../components/marketing/TestimonialCarousel';
import type { Testimonial } from '../../components/marketing/TestimonialCarousel';
import PersonIcon from '@mui/icons-material/Person';
import FlagIcon from '@mui/icons-material/Flag';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChatIcon from '@mui/icons-material/Chat';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import BlockIcon from '@mui/icons-material/Block';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const pitches = [
  {
    icon: <PersonIcon sx={{ fontSize: 64 }} />,
    title: 'Work with the same person',
    description: 'A real human — the same person handles all your requests',
  },
  {
    icon: <FlagIcon sx={{ fontSize: 64 }} />,
    title: '100% US Based Humans',
    description: 'All of our assistants are real people based in the USA',
  },
  {
    icon: <AccessTimeIcon sx={{ fontSize: 64 }} />,
    title: 'Billed to the minute',
    description: 'No task credits or confusing calculations',
  },
  {
    icon: <ChatIcon sx={{ fontSize: 64 }} />,
    title: 'Communicate on your terms',
    description: 'Use our apps, email, Slack, a shared spreadsheet - however you work is fine with us',
  },
  {
    icon: <GroupAddIcon sx={{ fontSize: 64 }} />,
    title: 'An extra project manager',
    description: "You'll get a project manager to help facilitate larger special projects",
  },
  {
    icon: <BlockIcon sx={{ fontSize: 64 }} />,
    title: 'No contract',
    description: 'Pay at the start of the month, cancel anytime',
  },
  {
    icon: <VerifiedUserIcon sx={{ fontSize: 64 }} />,
    title: 'HIPAA Certified',
    description: 'Assistants with HIPAA certification available',
  },
  {
    icon: <HelpOutlineIcon sx={{ fontSize: 64 }} />,
    title: "What else is included?",
    description: "Find out what's included with a dedicated assistant",
    link: '/dedicated-included',
  },
];

const testimonials: Testimonial[] = [
  {
    name: 'Alexis Ohanian',
    title: 'Founder of Reddit',
    content:
      "Fancy Hands allows me to hand off tasks like scheduling, calling, and research so I can focus on what's most important. As an entrepreneur, I swear by Fancy Hands. It's the best personal assistant service.",
    avatarUrl: '/marketing/testimonials/alexis-ohanian.png',
  },
  {
    name: 'Amanda Steinberg',
    title: 'Founder of DailyWorth',
    content:
      "I'm Fancy Hands' biggest fan. As a business owner and single mom, there's no time for appointment scheduling, research, or other home-related tasks. Before Fancy Hands, stuff just didn't get done. Now I use it almost daily and it saves me hours.",
    avatarUrl: '/marketing/testimonials/amanda-steinberg-avatar.png',
  },
  {
    name: 'Gabriel Weinberg',
    title: 'CEO & Founder, DuckDuckGo',
    content:
      'Fancy Hands has fundamentally changed the way I think about getting things done. In other words, they really changed my way of life.',
    avatarUrl: '/marketing/testimonials/gabriel-weinberg-avatar.png',
  },
];

const isDevMode = window.location.protocol === 'http:';

const STRIPE_PUBLISHABLE_KEY = isDevMode
  ? 'pk_akKhd7j6NV2bZqmFyKhpOQTCRzM8A'
  : 'pk_live_1bcjEGGekxeB5aNn0HGg1mmWzy5PGFgxE7b6pXh0GIBhMlIp6ohsOIDR7PyoPYHLMpkBfZwmdbNFlzwc8koO4oKDS00ZWRiVBrz';

const STRIPE_5_HOURS_BUTTON = isDevMode
  ? 'buy_btn_0NAdTdbcjEGGekxe9NbNRFK1'
  : 'buy_btn_0MspRAbcjEGGekxeEQxxf4WV';

const STRIPE_10_HOURS_BUTTON = isDevMode
  ? 'buy_btn_0NAdZ8bcjEGGekxe6EPQf0S6'
  : 'buy_btn_0MspVjbcjEGGekxeTIvhDDed';

export const DedicatedAssistantsPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Load Stripe Buy Button script
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/buy-button.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <Box>
      <HeroSection
        title="Dedicated Assistants"
        subtitle="A real person, dedicated to you. We match you with an experienced US-based assistant who learns your preferences and handles your tasks with human judgment and care. They can use your credit card, sign your NDA, and be available at a moment's notice."
        variant="gradient"
      >
        <Stack direction="row" spacing={1} justifyContent="center">
          <Chip label="🇺🇸 US-Based" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
          <Chip label="100% Human" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
          <Chip label="No AI" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
        </Stack>
      </HeroSection>

      {/* Pitch Grid */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={4}>
          {pitches.map((pitch, index) => (
            <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  cursor: pitch.link ? 'pointer' : 'default',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': pitch.link
                    ? { transform: 'translateY(-4px)', boxShadow: 4 }
                    : {},
                }}
                onClick={() => pitch.link && navigate(pitch.link)}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>{pitch.icon}</Box>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    {pitch.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {pitch.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Half Off Section */}
      <Box sx={{ bgcolor: 'primary.light', py: 6, textAlign: 'center', color: 'white' }}>
        <Container maxWidth="sm">
          <Typography variant="h4" gutterBottom fontWeight={700}>
            Half off your first month
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Ask about how to get 50% off your first month.
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              '&:hover': { bgcolor: 'grey.100' },
            }}
            href="https://zcform.com/0SMls"
            target="_blank"
          >
            Contact Us
          </Button>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 }, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Seen Enough?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
          Join us today with one of our general dedicated plans, or complete the form below and
          we'll be in touch to discuss even better, tailor-made solutions just for you!
        </Typography>

        {/* Stripe Buy Buttons */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center" sx={{ mb: 4 }}>
{/* @ts-expect-error - stripe-buy-button is a custom web component */}
          <stripe-buy-button
            buy-button-id={STRIPE_5_HOURS_BUTTON}
            publishable-key={STRIPE_PUBLISHABLE_KEY}
          />
          {/* @ts-expect-error - stripe-buy-button is a custom web component */}
          <stripe-buy-button
            buy-button-id={STRIPE_10_HOURS_BUTTON}
            publishable-key={STRIPE_PUBLISHABLE_KEY}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" flexWrap="wrap">
          <Button variant="outlined" size="large" onClick={() => navigate('/dedicated-included')}>
            What's Included?
          </Button>
          <Button
            variant="outlined"
            size="large"
            href="https://zcform.com/0SMls"
            target="_blank"
          >
            Schedule a Call to Learn More
          </Button>
        </Stack>
      </Container>

      {/* Testimonials */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography variant="h4" gutterBottom fontWeight={600} textAlign="center" sx={{ mb: 4 }}>
            What our clients say
          </Typography>
          <TestimonialCarousel testimonials={testimonials} />
        </Container>
      </Box>
    </Box>
  );
};
